/**
 * BL-028j Phase 2 (d) — Deals Missing Originator
 *
 * Surfaces the worklist of deals whose `originator_label = "unset"`.
 * Operators click into a deal to classify it via the standard
 * FullEditDealDialog (which now carries an Originator selector).
 *
 * Tile is read-only; no mutations from this surface. Refetches when
 * the page tab regains focus so the count keeps current as operators
 * resolve deals elsewhere in the app.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DealsMissingOriginator() {
  const { getAuthHeaders } = useAuth();
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/data-health/deals-missing-originator`, {
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDeals(d.deals || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error('Failed to load deals-missing-originator');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const openDeal = (deal) => {
    const url = `/deals/${deal.id}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Card data-testid="data-health-deals-missing-originator">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Deals Missing Originator
              <Badge variant={total > 0 ? 'secondary' : 'outline'} data-testid="dmo-count">
                {loading ? '…' : total}
              </Badge>
            </CardTitle>
            <CardDescription>
              Deals with <code>originator_label = &quot;unset&quot;</code> — needs operator
              classification (PFS / DIB / Enod / River / Parkside / Bluetie / Other).
              Click <em>Open</em> to set the originator in the Edit Deal dialog.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="dmo-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {total > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                data-testid="dmo-toggle-list"
              >
                {expanded ? 'Collapse' : 'Show list'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && total > 0 && (
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr className="border-b">
                  <th className="py-2 pr-3">Deal ID</th>
                  <th className="py-2 pr-3">Name / Merchant</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Funded</th>
                  <th className="py-2 pr-3">Funder?</th>
                  <th className="py-2 pr-3">Broker?</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody data-testid="dmo-list">
                {deals.map((d) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-3 font-mono text-xs">
                      {d.deal_id}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{d.deal_name || d.merchant_name}</div>
                      {d.merchant_name && d.merchant_name !== d.deal_name && (
                        <div className="text-xs text-muted-foreground">{d.merchant_name}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="text-xs">{d.status || '—'}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {d.funding_amount ? formatCurrency(d.funding_amount) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {d.has_funder_id ? '✓' : <span className="text-amber-600">missing</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {d.has_broker_id ? '✓' : <span className="text-amber-600">missing</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {d.created_at ? String(d.created_at).slice(0, 10) : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDeal(d)}
                        data-testid={`dmo-open-${d.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
